import { useState } from "react"

import { SidebarProvider } from "@/components/ui/sidebar"

interface AppShellProps {
  children: React.ReactNode
  variant?: "header" | "sidebar"
  // OKS-820 — generic passthrough merged onto the sidebar wrapper (via
  // `SidebarProvider`'s own `className` cn-merge). Route-agnostic: the caller
  // (e.g. `BrandShell` on the Messages route) decides when to override the
  // wrapper's default `min-h-svh` with a bounded `min-h-0 flex-1`. No route
  // knowledge lives here, so admin/agency shells are untouched.
  className?: string
}

export function AppShell({
  children,
  variant = "header",
  className,
}: AppShellProps) {
  // Sidebar open/closed state persists in `localStorage["sidebar"]` — a
  // documented per-device UX-ephemera carve-out from the no-browser-storage
  // rule. See `docs/frontend/README.md` §"Crew frontend hard rules" #5.
  const [isOpen, setIsOpen] = useState(() =>
    typeof window !== "undefined"
      ? localStorage.getItem("sidebar") !== "false"
      : true,
  )

  const handleSidebarChange = (open: boolean) => {
    setIsOpen(open)

    if (typeof window !== "undefined") {
      localStorage.setItem("sidebar", String(open))
    }
  }

  if (variant === "header") {
    return <div className="flex min-h-screen w-full flex-col">{children}</div>
  }

  return (
    <SidebarProvider
      defaultOpen={isOpen}
      open={isOpen}
      onOpenChange={handleSidebarChange}
      className={className}
    >
      {children}
    </SidebarProvider>
  )
}
